"use client";

import { c as _c } from "react/compiler-runtime";
import { Check } from "@untitledui/icons";
import { isValidElement, use } from "react";
import type { ListBoxItemProps as AriaListBoxItemProps } from "react-aria-components";
import { ListBoxItem as AriaListBoxItem, Text as AriaText } from "react-aria-components";
import { Avatar } from "@/components/base/avatar/avatar";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";
import { SelectContext, type SelectItemType } from "./select-shared";
const sizes = {
  sm: "p-2 pr-2.5",
  md: "p-2.5 pl-2",
  lg: "p-4 pl-2.5"
};
interface SelectItemProps extends Omit<AriaListBoxItemProps<SelectItemType>, "id" | "labelWrapperClassName" | "labelTextClassName">, SelectItemType {
  labelWrapperClassName?: string;
  labelTextClassName?: string;
}
export const SelectItem = t0 => {
  const $ = _c(42);
  let Icon;
  let avatarUrl;
  let children;
  let className;
  let id;
  let isDisabled;
  let label;
  let labelTextClassName;
  let labelWrapperClassName;
  let props;
  let supportingText;
  let textValueProp;
  let value;
  if ($[0] !== t0) {
    ({
      label,
      id,
      value,
      avatarUrl,
      supportingText,
      isDisabled,
      icon: Icon,
      className,
      children,
      labelWrapperClassName,
      labelTextClassName,
      textValue: textValueProp,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = avatarUrl;
    $[3] = children;
    $[4] = className;
    $[5] = id;
    $[6] = isDisabled;
    $[7] = label;
    $[8] = labelTextClassName;
    $[9] = labelWrapperClassName;
    $[10] = props;
    $[11] = supportingText;
    $[12] = textValueProp;
    $[13] = value;
  } else {
    Icon = $[1];
    avatarUrl = $[2];
    children = $[3];
    className = $[4];
    id = $[5];
    isDisabled = $[6];
    label = $[7];
    labelTextClassName = $[8];
    labelWrapperClassName = $[9];
    props = $[10];
    supportingText = $[11];
    textValueProp = $[12];
    value = $[13];
  }
  const {
    size
  } = use(SelectContext);
  const valueLabel = value && typeof value === "object" && "label" in value ? value.label : undefined;
  const labelOrChildren = label || valueLabel || (typeof children === "string" ? children : "");
  const textValue = textValueProp ?? (supportingText ? `${labelOrChildren} ${supportingText}` : labelOrChildren);
  let t1;
  if ($[14] !== Icon || $[15] !== avatarUrl || $[16] !== id || $[17] !== isDisabled || $[18] !== labelOrChildren || $[19] !== supportingText || $[20] !== value) {
    t1 = value ?? {
      id,
      label: labelOrChildren,
      avatarUrl,
      supportingText,
      isDisabled,
      icon: Icon
    };
    $[14] = Icon;
    $[15] = avatarUrl;
    $[16] = id;
    $[17] = isDisabled;
    $[18] = labelOrChildren;
    $[19] = supportingText;
    $[20] = value;
    $[21] = t1;
  } else {
    t1 = $[21];
  }
  let t2;
  if ($[22] !== className) {
    t2 = state => cx("w-full px-1.5 py-px outline-hidden", typeof className === "function" ? className(state) : className);
    $[22] = className;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[24] !== Icon || $[25] !== avatarUrl || $[26] !== children || $[27] !== label || $[28] !== labelOrChildren || $[29] !== labelTextClassName || $[30] !== labelWrapperClassName || $[31] !== size || $[32] !== supportingText) {
    t3 = state_0 => <div className={cx("flex cursor-pointer items-center gap-2 rounded-md outline-hidden select-none", state_0.isSelected && "bg-active", state_0.isDisabled && "cursor-not-allowed", state_0.isFocused && "bg-primary_hover", state_0.isFocusVisible && "ring-2 ring-focus-ring ring-inset", "*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary", state_0.isDisabled && "*:data-icon:text-fg-disabled", sizes[size])}>{avatarUrl ? <Avatar aria-hidden="true" size="xs" src={avatarUrl} alt={labelOrChildren} /> : isReactComponent(Icon) ? <Icon data-icon={true} aria-hidden="true" /> : isValidElement(Icon) ? Icon : null}<div className={cx("flex w-full min-w-0 flex-1 flex-wrap gap-x-2", labelWrapperClassName)}><AriaText slot="label" className={cx("truncate text-md font-normal whitespace-nowrap text-primary", state_0.isDisabled && "text-disabled", labelTextClassName)}>{label || (typeof children === "function" ? children(state_0) : children)}</AriaText>{supportingText && <AriaText slot="description" className={cx("text-md whitespace-nowrap text-tertiary", state_0.isDisabled && "text-disabled")}>{supportingText}</AriaText>}</div>{state_0.isSelected && <Check aria-hidden="true" className={cx("ml-auto text-fg-brand-primary", size === "sm" ? "size-4 stroke-[2.5px]" : "size-5", state_0.isDisabled && "text-fg-disabled")} />}</div>;
    $[24] = Icon;
    $[25] = avatarUrl;
    $[26] = children;
    $[27] = label;
    $[28] = labelOrChildren;
    $[29] = labelTextClassName;
    $[30] = labelWrapperClassName;
    $[31] = size;
    $[32] = supportingText;
    $[33] = t3;
  } else {
    t3 = $[33];
  }
  let t4;
  if ($[34] !== id || $[35] !== isDisabled || $[36] !== props || $[37] !== t1 || $[38] !== t2 || $[39] !== t3 || $[40] !== textValue) {
    t4 = <AriaListBoxItem id={id} value={t1} textValue={textValue} isDisabled={isDisabled} {...props} className={t2}>{t3}</AriaListBoxItem>;
    $[34] = id;
    $[35] = isDisabled;
    $[36] = props;
    $[37] = t1;
    $[38] = t2;
    $[39] = t3;
    $[40] = textValue;
    $[41] = t4;
  } else {
    t4 = $[41];
  }
  return t4;
};